import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Arrow,
  Circle,
  Ellipse,
  Group,
  Image as KonvaImage,
  Layer,
  Line,
  Path,
  Rect,
  Stage,
  Text,
  Transformer,
} from 'react-konva';
import type Konva from 'konva';
import {
  applyBridges,
  cableColor,
  cloudPath,
  computeLineBridges,
  computeSnap,
  connectorLineStyle,
  deviceInfoLines,
  edgeAnchor,
  expandSelection,
  findVlan,
  findVolume,
  hostTileRects,
  hostOvercommit,
  hostContentTop,
  HOST_HEADER_H,
  WARN_LINE_H,
  volumeTileRects,
  clusterStorageContentTop,
  volumeUsage,
  volumeVmCount,
  CS_HEADER_H,
  CS_WARN_LINE_H,
  inscribedRect,
  labelAlignment,
  legendRows,
  orthogonalRoute,
  retractArrowEnds,
  ARROW_GAP,
  portAnchorPoint,
  resolveEndpoint,
  resolveTaggedVlans,
  snapToGrid,
  switchPortRects,
  firewallInterfaceRects,
  firewallRuleCounts,
  firewallAnchorPoint,
  firewallLegendRules,
  firewallLegendNat,
  firewallLegendVpns,
  zoneColor,
  type BridgeMarker,
  type Box,
  type RemotePresence,
  type ResolvedConnector,
  type SceneDoc,
  type SceneNode,
  type SnapGuide,
} from '@system-atlas/shared';
import { ENV_COLORS, GRID_SIZE } from './colors';
import { clampZoom } from './canvasFormats';
import { useImageElement } from './useImageElement';
import { DeviceIcon } from './DeviceIcon';
import { FirewallIcon } from './FirewallIcon';
import { RemoteCursorsLayer } from './collab/RemoteCursorsLayer';

const SNAP_THRESHOLD = 6;
// Gestensteuerung: kurzes Druecken = Tippen, langes Halten/Ziehen = Navigieren/Markieren.
const LONG_PRESS_MS = 250;
const DRAG_THRESHOLD = 4; // Bildschirm-px, ab denen aus einem Klick ein Ziehen wird

/** Rendert einen Bild-Knoten (lädt das Bild und zeigt notfalls einen Platzhalter). */
function EditorImageShape({ node, stroke }: { node: SceneNode; stroke: string }) {
  const element = useImageElement(node.imageId);
  const env = ENV_COLORS[node.environmentType];
  return (
    <>
      {element ? (
        <KonvaImage image={element} width={node.width} height={node.height} stroke={stroke} strokeWidth={1} />
      ) : (
        <Rect
          width={node.width}
          height={node.height}
          fill="#f1f5f9"
          stroke={stroke}
          dash={[4, 4]}
          cornerRadius={6}
        />
      )}
      {node.label.length > 0 ? (
        <Text
          text={node.label}
          width={node.width}
          y={node.height - 18}
          align="center"
          fontSize={12}
          fill="#0f172a"
          listening={false}
        />
      ) : null}
      {node.environmentType !== 'neutral' ? (
        <Ellipse x={node.width - 9} y={9} radiusX={4} radiusY={4} fill={env.stroke} listening={false} />
      ) : null}
    </>
  );
}

/** Rendert ein Geraet als Bibliotheks-Bild (falls gesetzt + geladen), sonst als Strich-Icon. */
function DeviceShape({
  node,
  size,
  color,
  x,
  y,
}: {
  node: SceneNode;
  size: number;
  color: string;
  x: number;
  y: number;
}) {
  const element = useImageElement(node.device?.customImageId);
  if (node.device?.customImageId && element) {
    return <KonvaImage image={element} x={x} y={y} width={size} height={size} />;
  }
  return <DeviceIcon kind={node.device?.kind ?? 'other'} size={size} color={color} x={x} y={y} />;
}

/** Rendert einen Switch: Gehaeuse, Titel, zweireihige Ports (Nummern), SFP-Bereich rechts. */
function SwitchShape({
  node,
  vlans,
  highlight,
  selectedPortIndex,
  onPortMouseDown,
}: {
  node: SceneNode;
  vlans: SceneDoc['vlans'];
  highlight: boolean;
  selectedPortIndex: number | null;
  onPortMouseDown: (portIndex: number) => void;
}) {
  const sw = node.switch;
  if (!sw) return null;
  const box = { x: 0, y: 0, width: node.width, height: node.height };
  const rects = switchPortRects(box, sw);
  const byIndex = new Map(sw.ports.map((p) => [p.index, p]));
  const title = `${node.label || 'Switch'}  ·  L${sw.layer} · ${sw.role}`;
  return (
    <>
      <Rect
        width={node.width}
        height={node.height}
        fill="#ffffff"
        stroke={highlight ? '#0f172a' : '#334155'}
        strokeWidth={highlight ? 2 : 1.5}
        cornerRadius={8}
      />
      <Text
        text={title}
        x={6}
        y={4}
        width={node.width - 12}
        fontStyle="bold"
        fontSize={11}
        fill="#0f172a"
        listening={false}
        ellipsis
        wrap="none"
      />
      {rects.map((r) => {
        const port = byIndex.get(r.index);
        const vlan = findVlan(vlans, port?.vlanRef);
        const occupied = !!(port?.deviceId || port?.vlanRef);
        const fill = vlan?.color ?? (occupied ? '#cbd5e1' : '#f1f5f9');
        const baseStroke = port?.uplink && sw.role === 'core' ? '#b45309' : '#475569';
        const selected = selectedPortIndex === r.index;
        const tagged = resolveTaggedVlans(port?.taggedVlanRefs, vlans);
        const maxDots = 4;
        return (
          <Group
            key={r.index}
            onMouseDown={(e) => {
              e.cancelBubble = true;
              onPortMouseDown(r.index);
            }}
          >
            <Rect
              x={r.x}
              y={r.y}
              width={r.width}
              height={r.height}
              fill={fill}
              stroke={selected ? '#0f172a' : baseStroke}
              strokeWidth={selected ? 2 : 1}
              cornerRadius={r.sfp ? 1 : 2}
              dash={r.sfp ? [3, 2] : undefined}
            />
            <Text
              text={String(r.index)}
              x={r.x}
              y={r.y + r.height / 2 - 4}
              width={r.width}
              align="center"
              fontSize={8}
              fill="#0f172a"
              listening={false}
            />
            {tagged.slice(0, maxDots).map((v, i) => (
              <Circle key={v.id} x={r.x + 4 + i * 5} y={r.y + 3} radius={1.8} fill={v.color} listening={false} />
            ))}
            {tagged.length > maxDots ? (
              <Text
                text={`+${tagged.length - maxDots}`}
                x={r.x + 4 + maxDots * 5}
                y={r.y}
                fontSize={6}
                fill="#0f172a"
                listening={false}
              />
            ) : null}
            {r.sfp && port?.sfpModule ? (
              <Text
                text={port.sfpModule}
                x={r.x}
                y={r.y + r.height - 8}
                width={r.width}
                align="center"
                fontSize={6}
                fill="#475569"
                listening={false}
                ellipsis
                wrap="none"
              />
            ) : null}
          </Group>
        );
      })}
    </>
  );
}

/** Host/VM-Element (Baustein E): Kopfzeile (Name · OS · Hypervisor) + Kachel-Grid der VMs. */
function HostShape({
  node,
  vlans,
  allNodes,
  highlight,
  activeLabel,
  inactiveLabel,
  cpuOverLabel,
  ramOverLabel,
  vlanLabel,
  storageLabel,
}: {
  node: SceneNode;
  vlans: SceneDoc['vlans'];
  allNodes: SceneNode[];
  highlight: boolean;
  activeLabel: string;
  inactiveLabel: string;
  cpuOverLabel: string;
  ramOverLabel: string;
  vlanLabel: string;
  storageLabel: string;
}) {
  const host = node.host;
  if (!host) return null;
  const box = { x: 0, y: 0, width: node.width, height: node.height };
  const oc = hostOvercommit(host);
  const overcommit = oc.cpu || oc.ram;
  const topOffset = hostContentTop(host);
  const rects = hostTileRects(box, host.vms.length, topOffset);
  const head = [node.label || 'Host', host.os, host.hypervisor, host.ip].filter(Boolean).join('  ·  ');
  const warnings = [oc.cpu ? cpuOverLabel : null, oc.ram ? ramOverLabel : null].filter(Boolean) as string[];
  return (
    <>
      <Rect
        width={node.width}
        height={node.height}
        fill="#ffffff"
        stroke={overcommit ? '#dc2626' : highlight ? '#0f172a' : '#334155'}
        strokeWidth={overcommit || highlight ? 2 : 1.5}
        cornerRadius={8}
      />
      <Rect width={node.width} height={HOST_HEADER_H} fill="#f1f5f9" cornerRadius={8} listening={false} />
      <Text
        text={head}
        x={8}
        y={6}
        width={node.width - 16}
        fontStyle="bold"
        fontSize={11}
        fill="#0f172a"
        listening={false}
        ellipsis
        wrap="none"
      />
      {warnings.map((w, i) => (
        <Text
          key={w}
          text={w}
          x={8}
          y={HOST_HEADER_H + i * WARN_LINE_H}
          width={node.width - 16}
          fontStyle="bold"
          fontSize={10}
          fill="#dc2626"
          listening={false}
          ellipsis
          wrap="none"
        />
      ))}
      {rects.map((r) => {
        const vm = host.vms[r.index]!;
        const vlan = findVlan(vlans, vm.vlanRef);
        const volume = findVolume(allNodes, vm.volumeRef);
        const wide = r.width > 120;
        const fill = vm.active ? '#ecfdf5' : '#f1f5f9';
        const stroke = vm.active ? '#10b981' : '#cbd5e1';
        // Zeilen von oben nach unten: Name, OS, CPU/RAM, VLAN, Storage, Status.
        // Hoehen-adaptiv: schmale/niedrige Bestandskacheln zeigen nur, was hineinpasst
        // (Storage-Zeile braucht am meisten Platz), damit kein Text abgeschnitten wird.
        const hw = [vm.cores != null ? `${vm.cores}C` : null, vm.ram != null ? `${vm.ram} GB` : null].filter(Boolean).join(' · ');
        const showOs = r.height >= 40 && !!vm.os;
        const showHw = r.height >= 50 && !!hw;
        const showVlan = r.height >= 60 && !!vlan;
        const showStorage = r.height >= 70 && !!volume;
        return (
          <Group key={vm.id} listening={false}>
            <Rect x={r.x} y={r.y} width={r.width} height={r.height} fill={fill} stroke={stroke} strokeWidth={1} cornerRadius={4} />
            <Text text={vm.name || 'VM'} x={r.x + 6} y={r.y + 3} width={r.width - 12} fontSize={10} fontStyle="bold" fill="#0f172a" ellipsis wrap="none" />
            {showOs ? (
              <Text text={vm.os!} x={r.x + 6} y={r.y + 15} width={r.width - 12} fontSize={9} fill="#475569" ellipsis wrap="none" />
            ) : null}
            {showHw ? (
              <Text text={hw} x={r.x + 6} y={r.y + 25} width={r.width - 12} fontSize={9} fill="#64748b" ellipsis wrap="none" />
            ) : null}
            {/* VLAN-Zeile: 'VLAN <punkt> <id>' bzw. nur Punkt auf schmalen Kacheln */}
            {showVlan ? (
              wide ? (
                <>
                  <Text text={vlanLabel} x={r.x + 6} y={r.y + 36} fontSize={9} fill="#64748b" listening={false} />
                  <Circle x={r.x + 48} y={r.y + 40} radius={3.5} fill={vlan!.color} />
                  <Text text={String(vlan!.vlanId)} x={r.x + 56} y={r.y + 36} width={r.width - 62} fontSize={9} fill="#475569" ellipsis wrap="none" />
                </>
              ) : (
                <Circle x={r.x + 8} y={r.y + 40} radius={3.5} fill={vlan!.color} />
              )
            ) : null}
            {/* Storage-Zeile: 'Clusterstorage <punkt> <name>' bzw. nur Punkt (Label breitengedeckelt, damit der Punkt nie ueberlappt) */}
            {showStorage ? (
              wide ? (
                <>
                  <Text text={storageLabel} x={r.x + 6} y={r.y + 47} width={74} fontSize={9} fill="#64748b" ellipsis wrap="none" listening={false} />
                  <Circle x={r.x + 84} y={r.y + 51} radius={3.5} fill={volume!.color} />
                  <Text text={volume!.name} x={r.x + 92} y={r.y + 47} width={r.width - 98} fontSize={9} fill="#475569" ellipsis wrap="none" />
                </>
              ) : (
                <Circle x={r.x + 8} y={r.y + 51} radius={3.5} fill={volume!.color} />
              )
            ) : null}
            <Text text={vm.active ? activeLabel : inactiveLabel} x={r.x + 6} y={r.y + r.height - 12} width={r.width - 12} fontSize={8} fill={vm.active ? '#059669' : '#94a3b8'} ellipsis wrap="none" />
          </Group>
        );
      })}
    </>
  );
}

/** Clusterstorage (Baustein H): Kopfzeile + Kachel-Grid der Volumes (Auslastungsbalken, Farbpunkt, VM-Zahl). */
function ClusterStorageShape({
  node,
  allNodes,
  highlight,
  kindLabels,
  standLabel,
  fullLabel,
  vmCountLabel,
  redundancyLabels,
}: {
  node: SceneNode;
  allNodes: SceneNode[];
  highlight: boolean;
  kindLabels: Record<string, string>;
  standLabel: string;
  fullLabel: string;
  vmCountLabel: (n: number) => string;
  redundancyLabels: Record<string, string>;
}) {
  const data = node.clusterStorage;
  if (!data) return null;
  const box = { x: 0, y: 0, width: node.width, height: node.height };
  const topOffset = clusterStorageContentTop(data);
  const rects = volumeTileRects(box, data.volumes.length, topOffset);
  const anyWarn = data.volumes.some((v) => volumeUsage(v).warn);
  const kindText = data.kind ? kindLabels[data.kind] ?? data.kind : '';
  const head = [node.label || 'Clusterstorage', kindText, data.cluster].filter(Boolean).join('  ·  ');
  const warnVols = data.volumes.filter((v) => volumeUsage(v).warn);
  return (
    <>
      <Rect
        width={node.width}
        height={node.height}
        fill="#ffffff"
        stroke={anyWarn ? '#dc2626' : highlight ? '#0f172a' : '#334155'}
        strokeWidth={anyWarn || highlight ? 2 : 1.5}
        cornerRadius={8}
      />
      <Rect width={node.width} height={CS_HEADER_H} fill="#f1f5f9" cornerRadius={8} listening={false} />
      <Text text={head} x={8} y={5} width={node.width - 16} fontStyle="bold" fontSize={11} fill="#0f172a" listening={false} ellipsis wrap="none" />
      {data.importedAt ? (
        <Text text={`${standLabel}: ${data.importedAt}`} x={8} y={17} width={node.width - 16} fontSize={9} fill="#64748b" listening={false} ellipsis wrap="none" />
      ) : null}
      {warnVols.map((v, i) => (
        <Text
          key={v.id}
          text={`${v.name}: ${fullLabel}`}
          x={8}
          y={CS_HEADER_H + i * CS_WARN_LINE_H}
          width={node.width - 16}
          fontStyle="bold"
          fontSize={10}
          fill="#dc2626"
          listening={false}
          ellipsis
          wrap="none"
        />
      ))}
      {rects.map((r) => {
        const vol = data.volumes[r.index]!;
        const usage = volumeUsage(vol);
        const meta = [vol.fs, vol.sizeGb != null ? `${vol.sizeGb} GB` : null, vol.redundancy ? (redundancyLabels[vol.redundancy] ?? vol.redundancy) : null].filter(Boolean).join(' · ');
        const barW = Math.max(0, Math.min(1, usage.percent / 100)) * (r.width - 12);
        const vmN = volumeVmCount(allNodes, vol.id);
        return (
          <Group key={vol.id} listening={false}>
            <Rect x={r.x} y={r.y} width={r.width} height={r.height} fill="#f8fafc" stroke="#cbd5e1" strokeWidth={1} cornerRadius={4} />
            <Circle x={r.x + 8} y={r.y + 8} radius={3.5} fill={vol.color} />
            <Text text={vol.name || 'Volume'} x={r.x + 16} y={r.y + 4} width={r.width - 22} fontSize={10} fontStyle="bold" fill="#0f172a" ellipsis wrap="none" />
            {meta ? <Text text={meta} x={r.x + 6} y={r.y + 18} width={r.width - 12} fontSize={9} fill="#475569" ellipsis wrap="none" /> : null}
            {/* Auslastungsbalken */}
            <Rect x={r.x + 6} y={r.y + 32} width={r.width - 12} height={8} fill="#e2e8f0" cornerRadius={2} />
            <Rect x={r.x + 6} y={r.y + 32} width={barW} height={8} fill={usage.warn ? '#dc2626' : '#0d9488'} cornerRadius={2} />
            <Text text={`${usage.percent}%`} x={r.x + 6} y={r.y + 42} width={r.width - 12} fontSize={9} fill={usage.warn ? '#dc2626' : '#64748b'} wrap="none" />
            <Text text={vmCountLabel(vmN)} x={r.x + 6} y={r.y + r.height - 13} width={r.width - 12} fontSize={8} fill="#94a3b8" ellipsis wrap="none" />
          </Group>
        );
      })}
    </>
  );
}

/** Switch-Legende (C5.3): Tabelle Port | VLAN | Tagged VLAN | Dose, gespeist aus legendRows. */
function SwitchLegendShape({
  node,
  sourceNode,
  vlans,
}: {
  node: SceneNode;
  sourceNode: SceneNode | undefined;
  vlans: SceneDoc['vlans'];
}) {
  const { t } = useTranslation();
  const legend = node.switchLegend ?? {};
  const rows = sourceNode?.switch ? legendRows(sourceNode.switch, vlans, legend.showAllPorts ?? false) : [];
  const colPort = 10;
  const colVlan = 70;
  const colTagged = 130;
  const colDose = node.width - 90;
  const labelColor = '#64748b';
  const rowH = 20;
  const maxTag = 4;
  return (
    <>
      <Rect width={node.width} height={node.height} fill="#ffffff" stroke="#cbd5e1" strokeWidth={1} cornerRadius={8} />
      <Text
        text={node.label || sourceNode?.label || t('editor.switchLegendTitle')}
        x={10}
        y={8}
        fontStyle="bold"
        fontSize={13}
        fill="#0f172a"
        listening={false}
      />
      {!sourceNode?.switch ? (
        <Text
          text={legend.sourceNodeId ? t('editor.switchLegend.notFound') : t('editor.switchLegend.noSwitch')}
          x={10}
          y={34}
          fontSize={12}
          fill="#94a3b8"
          listening={false}
        />
      ) : (
        <>
          <Group x={0} y={30} listening={false}>
            <Text text={t('editor.switchLegend.colPort')} x={colPort} fontSize={10} fontStyle="bold" fill={labelColor} />
            <Text text={t('editor.switchLegend.colVlan')} x={colVlan} fontSize={10} fontStyle="bold" fill={labelColor} />
            <Text text={t('editor.switchLegend.colTagged')} x={colTagged} fontSize={10} fontStyle="bold" fill={labelColor} />
            <Text text={t('editor.switchLegend.colDose')} x={colDose} fontSize={10} fontStyle="bold" fill={labelColor} />
          </Group>
          <Line points={[10, 44, node.width - 10, 44]} stroke="#e2e8f0" strokeWidth={1} listening={false} />
          {rows.map((row, i) => (
            <Group key={row.index} x={0} y={50 + i * rowH} listening={false}>
              <Text
                text={`${row.index}${row.portType === 'sfp' ? ' SFP' : ''}`}
                x={colPort}
                fontSize={11}
                fill="#0f172a"
              />
              {row.accessVlan ? (
                <>
                  <Circle x={colVlan + 4} y={6} radius={4} fill={row.accessVlan.color} />
                  <Text text={String(row.accessVlan.vlanId)} x={colVlan + 12} fontSize={11} fill="#0f172a" />
                </>
              ) : null}
              {row.taggedVlans.slice(0, maxTag).map((v, j) => (
                <Circle key={v.id} x={colTagged + 4 + j * 10} y={6} radius={4} fill={v.color} />
              ))}
              {row.taggedVlans.length > maxTag ? (
                <Text
                  text={`+${row.taggedVlans.length - maxTag}`}
                  x={colTagged + 4 + maxTag * 10}
                  fontSize={10}
                  fill={labelColor}
                />
              ) : null}
              {row.dose ? <Text text={row.dose} x={colDose} fontSize={11} fill="#0f172a" width={80} ellipsis wrap="none" /> : null}
            </Group>
          ))}
        </>
      )}
    </>
  );
}

/** Rendert eine Firewall: Gehaeuse, Rollen-Icon, Kopf (Name · Rolle · Modell + HA), Interface-Slots, Zaehler. */
function FirewallShape({
  node,
  highlight,
  selectedPortIndex,
  onPortMouseDown,
}: {
  node: SceneNode;
  highlight: boolean;
  selectedPortIndex: number | null;
  onPortMouseDown: (portIndex: number) => void;
}) {
  const fw = node.firewall;
  if (!fw) return null;
  const box = { x: 0, y: 0, width: node.width, height: node.height };
  const rects = firewallInterfaceRects(box, fw.interfaces);
  const counts = firewallRuleCounts(fw);
  const iconSize = 22;
  const titleParts = [node.label || 'Firewall', fw.role.toUpperCase()];
  if (fw.model) titleParts.push(fw.model);
  const footerParts = [`${counts.rules} Regeln`];
  if (counts.nat > 0) footerParts.push(`${counts.nat} NAT`);
  footerParts.push(`${counts.vpns} VPNs`);
  return (
    <>
      <Rect
        width={node.width}
        height={node.height}
        fill="#ffffff"
        stroke={highlight ? '#0f172a' : '#334155'}
        strokeWidth={highlight ? 2 : 1.5}
        cornerRadius={8}
      />
      <FirewallIcon role={fw.role} size={iconSize} color="#334155" x={6} y={6} />
      <Text
        text={titleParts.join('  ·  ')}
        x={6 + iconSize + 6}
        y={8}
        width={node.width - iconSize - 18}
        fontStyle="bold"
        fontSize={11}
        fill="#0f172a"
        listening={false}
        ellipsis
        wrap="none"
      />
      {fw.haRole && fw.haRole !== 'standalone' ? (
        <Text text={`HA ${fw.haRole}`} x={6 + iconSize + 6} y={22} fontSize={9} fill="#8b5cf6" listening={false} />
      ) : null}
      {rects.map((r) => {
        const iface = fw.interfaces[r.index - 1]!;
        const color = zoneColor(iface.zone);
        const selected = selectedPortIndex === r.index;
        return (
          <Group
            key={r.id}
            onMouseDown={(e) => {
              e.cancelBubble = true;
              onPortMouseDown(r.index);
            }}
          >
            <Rect
              x={r.x}
              y={r.y}
              width={r.width}
              height={r.height}
              fill="#f8fafc"
              stroke={selected ? '#0f172a' : color}
              strokeWidth={selected ? 2 : 1.5}
              cornerRadius={2}
            />
            <Text
              text={iface.name}
              x={r.x}
              y={r.y + r.height / 2 - 4}
              width={r.width}
              align="center"
              fontSize={8}
              fill="#0f172a"
              listening={false}
              ellipsis
              wrap="none"
            />
          </Group>
        );
      })}
      <Text text={footerParts.join(' · ')} x={6} y={node.height - 14} fontSize={9} fill="#64748b" listening={false} />
    </>
  );
}

/** Rendert die Firewall-Legende (Baustein F): Abschnitte Regeln/NAT/VPNs je nach `sections`. */
function FirewallLegendShape({ node, sourceNode }: { node: SceneNode; sourceNode: SceneNode | undefined }) {
  const { t } = useTranslation();
  const legend = node.firewallLegend ?? {};
  const sections = legend.sections ?? ['rules'];
  const showDisabled = legend.showDisabled ?? false;
  const fw = sourceNode?.firewall;
  const rows: { key: string; text: string; dot?: string; head?: boolean }[] = [];
  if (fw) {
    if (sections.includes('rules')) {
      rows.push({ key: 'h-rules', text: t('editor.firewallLegend.rules'), head: true });
      firewallLegendRules(fw, showDisabled).forEach((r) =>
        rows.push({
          key: `r-${r.id}`,
          text: `${r.source || 'any'} → ${r.destination || 'any'} : ${r.service || r.port || 'any'}`,
          dot: r.action === 'allow' ? '#16a34a' : '#dc2626',
        }),
      );
    }
    if (sections.includes('nat')) {
      rows.push({ key: 'h-nat', text: t('editor.firewallLegend.nat'), head: true });
      firewallLegendNat(fw, showDisabled).forEach((n) =>
        rows.push({
          key: `n-${n.id}`,
          text: `${n.name || ''} ${n.externalPort || ''} → ${n.internalHost || ''}:${n.internalPort || ''}`.trim(),
        }),
      );
    }
    if (sections.includes('vpns')) {
      rows.push({ key: 'h-vpns', text: t('editor.firewallLegend.vpns'), head: true });
      firewallLegendVpns(fw).forEach((v) =>
        rows.push({ key: `p-${v.id}`, text: `${v.name} (${v.kind})${v.peer ? ' ' + v.peer : ''}` }),
      );
    }
  }
  const rowH = 16;
  return (
    <>
      <Rect width={node.width} height={node.height} fill="#ffffff" stroke="#cbd5e1" strokeWidth={1} cornerRadius={8} />
      <Text
        text={node.label || sourceNode?.label || t('editor.firewallLegendTitle')}
        x={10}
        y={8}
        fontStyle="bold"
        fontSize={13}
        fill="#0f172a"
        listening={false}
      />
      {!fw ? (
        <Text
          text={legend.sourceNodeId ? t('editor.firewallLegend.notFound') : t('editor.firewallLegend.noFirewall')}
          x={10}
          y={34}
          fontSize={12}
          fill="#94a3b8"
          listening={false}
        />
      ) : (
        rows.map((row, i) => (
          <Group key={row.key} x={0} y={40 + i * rowH} listening={false}>
            {row.dot ? <Circle x={14} y={-4} radius={3} fill={row.dot} /> : null}
            <Text
              text={row.text}
              x={row.head ? 10 : 22}
              fontSize={row.head ? 11 : 10}
              fontStyle={row.head ? 'bold' : 'normal'}
              fill={row.head ? '#334155' : '#0f172a'}
              width={node.width - 30}
              ellipsis
              wrap="none"
            />
          </Group>
        ))
      )}
    </>
  );
}

interface EditorCanvasProps {
  scene: SceneDoc;
  selectedIds: string[];
  pendingFrom: { nodeId: string; portIndex?: number } | null;
  onContextMenu: (info: { clientX: number; clientY: number; nodeId: string | null }) => void;
  /** Objekte verschiebbar (Auswahl-Modus, nicht readOnly). Greift nur am ausgewaehlten Knoten. */
  draggable: boolean;
  /** Verbinden-Werkzeug aktiv: kurzer Tap auf Knoten/Port verbindet, Ziehen navigiert weiter. */
  connectMode: boolean;
  /** Kommentar-Werkzeug aktiv: kurzer Tap setzt einen Kommentar-Pin statt auszuwaehlen. */
  commentMode: boolean;
  /** Tap im Kommentar-Modus: Weltkoordinaten + getroffener Knoten (oder null = freier Punkt). */
  onCommentAt?: (info: { world: { x: number; y: number }; nodeId: string | null }) => void;
  /** Leertaste gedrueckt: erzwingt Navigieren (Pan) auch ueber ausgewaehlten Objekten. */
  panViaSpace: boolean;
  /** Waehrend PNG/PDF-Export: Auswahl-Highlight, Transformer, Hilfslinien und Marquee ausblenden. */
  exporting: boolean;
  viewportWidth: number;
  viewportHeight: number;
  scale: number;
  stagePos: { x: number; y: number };
  canvas: { x: number; y: number; width: number; height: number };
  stageRef?: RefObject<Konva.Stage | null>;
  onNodeMouseDown: (id: string, shiftKey: boolean) => void;
  onPortMouseDown: (nodeId: string, portIndex: number) => void;
  selectedPort: { nodeId: string; portIndex: number } | null;
  onConnectorMouseDown: (id: string) => void;
  onEmptyMouseDown: () => void;
  onMarqueeSelect: (ids: string[]) => void;
  onNodesChange: (changes: { id: string; patch: Partial<SceneNode> }[]) => void;
  onConnectorChange: (id: string, patch: Partial<SceneDoc['connectors'][number]>) => void;
  onViewportChange: (scale: number, pos: { x: number; y: number }) => void;
  /** Live-Zeigerposition in Weltkoordinaten fuer die Kollaboration (null = Zeiger weg vom Canvas). */
  onPointerWorldMove?: (p: { x: number; y: number } | null) => void;
  /** Cursor der anderen Teilnehmer (Weltkoordinaten); gerendert auf eigener Ebene, nie im Export. */
  remoteCursors?: RemotePresence[];
  /** Fremde Auswahl (Phase 3): Objekt-ID -> Teilnehmerfarbe; Outline/Unterlegung, nie im Export. */
  remoteSelections?: ReadonlyMap<string, string>;
}

function toBox(n: SceneNode): Box {
  return { x: n.x, y: n.y, width: n.width, height: n.height };
}

interface MarqueeRect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

function normalizeRect(m: MarqueeRect): Box {
  return {
    x: Math.min(m.x1, m.x2),
    y: Math.min(m.y1, m.y2),
    width: Math.abs(m.x2 - m.x1),
    height: Math.abs(m.y2 - m.y1),
  };
}

function intersects(a: Box, b: Box): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

function contains(outer: Box, inner: Box): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.width <= outer.x + outer.width &&
    inner.y + inner.height <= outer.y + outer.height
  );
}

export function EditorCanvas(props: EditorCanvasProps) {
  const {
    scene,
    selectedIds,
    pendingFrom,
    draggable,
    connectMode,
    panViaSpace,
    exporting,
    viewportWidth,
    viewportHeight,
    scale,
    stagePos,
    canvas,
  } = props;
  const pendingFromId = pendingFrom?.nodeId ?? null;
  const { t } = useTranslation();
  const localRef = useRef<Konva.Stage>(null);
  const stageRef = props.stageRef ?? localRef;
  const trRef = useRef<Konva.Transformer>(null);
  const [guides, setGuides] = useState<SnapGuide[]>([]);
  const [marquee, setMarquee] = useState<MarqueeRect | null>(null);
  // Live-Zeigerposition (Weltkoordinaten) für die Connect-Vorschaulinie.
  const [connectPointer, setConnectPointer] = useState<{ x: number; y: number } | null>(null);
  // Pinch-Zoom/Zwei-Finger-Pan: letzter Abstand + Mittelpunkt (Bildschirmkoordinaten).
  const pinchRef = useRef<{ dist: number; cx: number; cy: number } | null>(null);
  const [pinching, setPinching] = useState(false);
  // Multi-Drag: Ausgangspositionen aller mitbewegten Knoten je Geste.
  const dragRef = useRef<{ grabbedId: string; orig: Map<string, { x: number; y: number }> } | null>(null);
  // Gestensteuerung (siehe beginPointerGesture): laeuft ein Konva-Objekt-Move? + PanReady nach Long-Press.
  const objectDragRef = useRef(false);
  // Aufraeumfunktion der aktiven Maus-Geste (Listener + Timer); fuer mouseup und Unmount.
  const gestureCleanupRef = useRef<(() => void) | null>(null);
  // Wurde die letzte Rechts-Geste ein Markierungsrahmen? Dann das folgende contextmenu unterdruecken.
  const rightMarqueeRef = useRef(false);
  const [panReady, setPanReady] = useState(false); // Long-Press: ab jetzt navigiert linkes Ziehen
  const [panningActive, setPanningActive] = useState(false); // gerade am Navigieren (Cursor 'grabbing')

  const nodeMap = useMemo(
    () => new Map(scene.nodes.map((n) => [n.id, { x: n.x, y: n.y, width: n.width, height: n.height }])),
    [scene.nodes],
  );
  const shapeMap = useMemo(
    () => new Map(scene.nodes.map((n) => [n.id, n.type === 'circle' ? ('ellipse' as const) : ('rect' as const)])),
    [scene.nodes],
  );
  const switchMap = useMemo(
    () => new Map(scene.nodes.filter((n) => n.switch).map((n) => [n.id, n.switch!])),
    [scene.nodes],
  );
  const firewallMap = useMemo(
    () => new Map(scene.nodes.filter((n) => n.firewall).map((n) => [n.id, n.firewall!])),
    [scene.nodes],
  );
  // IDs aller Verbinder – fuer den zentralen Treffer-Lookup der Maus-Geste.
  const connectorIds = useMemo(() => new Set(scene.connectors.map((c) => c.id)), [scene.connectors]);

  // Transformer an alle ausgewählten Knoten heften (nur im Auswahl-Modus).
  useEffect(() => {
    const tr = trRef.current;
    const stage = stageRef.current;
    if (!tr || !stage) return;
    const targets =
      draggable && !exporting
        ? selectedIds
            .filter((id) => nodeMap.has(id))
            .map((id) => stage.findOne(`#${id}`))
            .filter((n): n is Konva.Node => !!n)
        : [];
    tr.nodes(targets);
    tr.getLayer()?.batchDraw();
  }, [selectedIds, draggable, exporting, scene, stageRef, nodeMap]);

  const gridLines = useMemo(() => {
    const step = GRID_SIZE * 4;
    const x0 = canvas.x;
    const y0 = canvas.y;
    const x1 = canvas.x + canvas.width;
    const y1 = canvas.y + canvas.height;
    const lines: number[][] = [];
    for (let x = x0 + step; x < x1; x += step) lines.push([x, y0, x, y1]);
    for (let y = y0 + step; y < y1; y += step) lines.push([x0, y, x1, y]);
    return lines;
  }, [canvas.x, canvas.y, canvas.width, canvas.height]);

  function handleWheel(e: Konva.KonvaEventObject<WheelEvent>) {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const mousePointTo = { x: (pointer.x - stagePos.x) / scale, y: (pointer.y - stagePos.y) / scale };
    const newScale = clampZoom(e.evt.deltaY > 0 ? scale / 1.1 : scale * 1.1);
    props.onViewportChange(newScale, {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    });
  }

  // Connect-Vorschau zurücksetzen, sobald keine Quelle mehr gewählt ist.
  useEffect(() => {
    if (!pendingFrom) setConnectPointer(null);
  }, [pendingFrom]);

  // Ankerpunkt der Connect-Quelle (Port-genau bei Switch-Ports, sonst Kantenpunkt zum Zeiger).
  function connectSourceAnchor(): { x: number; y: number } | null {
    if (!pendingFrom) return null;
    const box = nodeMap.get(pendingFrom.nodeId);
    if (!box) return null;
    const sw = switchMap.get(pendingFrom.nodeId);
    if (pendingFrom.portIndex !== undefined && sw) return portAnchorPoint(box, sw, pendingFrom.portIndex);
    const fw = firewallMap.get(pendingFrom.nodeId);
    if (pendingFrom.portIndex !== undefined && fw) return firewallAnchorPoint(box, fw.interfaces, pendingFrom.portIndex);
    const toward = connectPointer ?? { x: box.x + box.width / 2, y: box.y + box.height / 2 };
    return edgeAnchor(box, toward, { shape: shapeMap.get(pendingFrom.nodeId) });
  }

  // --- Touch: Pinch-Zoom + Zwei-Finger-Pan (isoliert von der Maussteuerung) --
  function handleTouchMove(e: Konva.KonvaEventObject<TouchEvent>) {
    const touches = e.evt.touches;
    if (touches.length < 2) return;
    e.evt.preventDefault();
    const stage = stageRef.current;
    const t1 = touches[0];
    const t2 = touches[1];
    if (!stage || !t1 || !t2) return;
    const rect = stage.container().getBoundingClientRect();
    const p1 = { x: t1.clientX - rect.left, y: t1.clientY - rect.top };
    const p2 = { x: t2.clientX - rect.left, y: t2.clientY - rect.top };
    const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const cx = (p1.x + p2.x) / 2;
    const cy = (p1.y + p2.y) / 2;
    if (!pinching) setPinching(true);
    const prev = pinchRef.current;
    if (prev) {
      const factor = prev.dist > 0 ? dist / prev.dist : 1;
      const newScale = clampZoom(scale * factor);
      // Um den vorherigen Mittelpunkt zoomen, dann um die Mittelpunkt-Verschiebung pannen.
      const worldX = (prev.cx - stagePos.x) / scale;
      const worldY = (prev.cy - stagePos.y) / scale;
      props.onViewportChange(newScale, {
        x: prev.cx - worldX * newScale + (cx - prev.cx),
        y: prev.cy - worldY * newScale + (cy - prev.cy),
      });
    }
    pinchRef.current = { dist, cx, cy };
  }

  function handleTouchEnd(e: Konva.KonvaEventObject<TouchEvent>) {
    if (e.evt.touches.length < 2) {
      pinchRef.current = null;
      if (pinching) setPinching(false);
    }
  }

  // --- Pan per mittlerer Maustaste (immer verfügbar) ------------------------
  function startMiddlePan(e: Konva.KonvaEventObject<MouseEvent>) {
    e.evt.preventDefault();
    const start = { x: e.evt.clientX, y: e.evt.clientY };
    const orig = { ...stagePos };
    const onMove = (ev: MouseEvent) => {
      props.onViewportChange(scale, { x: orig.x + (ev.clientX - start.x), y: orig.y + (ev.clientY - start.y) });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  // --- Rahmenauswahl ---------------------------------------------------------
  function worldPointer(): { x: number; y: number } | null {
    const stage = stageRef.current;
    const pointer = stage?.getPointerPosition();
    if (!pointer) return null;
    return { x: (pointer.x - stagePos.x) / scale, y: (pointer.y - stagePos.y) / scale };
  }

  // Wertet ein (bereits normalisiertes) Markierungs-Rechteck in Weltkoordinaten aus.
  // Das Rechteck kommt aus der Geste-Closure, nicht aus dem State (vermeidet Stale-Closure).
  function finishMarquee(rect: Box) {
    if (rect.width < 3 && rect.height < 3) {
      // Winziger Zug = Klick auf leere Fläche → Auswahl leeren.
      props.onEmptyMouseDown();
      return;
    }
    // Zonen nur bei Voll-Umfassung, alle anderen bereits bei Überschneidung.
    const ids = scene.nodes
      .filter((n) => (n.type === 'zone' ? contains(rect, toBox(n)) : intersects(rect, toBox(n))))
      .map((n) => n.id);
    props.onMarqueeSelect(ids);
  }

  // True, wenn das Ereignis-Ziel zu den Transformer-Resize-Ankern gehoert (Konva macht das Resize).
  function isTransformerTarget(target: Konva.Node | null): boolean {
    let n: Konva.Node | null = target;
    while (n) {
      if (n.getClassName() === 'Transformer') return true;
      n = n.getParent();
    }
    return false;
  }

  // Rechte Maustaste: Kontextmenue an der Zeigerposition (ausser die Geste war ein Markierungsrahmen).
  function handleContextMenu(e: Konva.KonvaEventObject<MouseEvent>) {
    e.evt.preventDefault();
    if (rightMarqueeRef.current) {
      rightMarqueeRef.current = false;
      return;
    }
    const tgt = targetAt(e.target);
    const nodeId = tgt?.type === 'node' ? tgt.id : null;
    props.onContextMenu({ clientX: e.evt.clientX, clientY: e.evt.clientY, nodeId });
  }

  // Weltkoordinaten aus absoluten Bildschirm-Koordinaten (robust auch ausserhalb der Stage).
  function clientToWorld(clientX: number, clientY: number): { x: number; y: number } | null {
    const stage = stageRef.current;
    if (!stage) return null;
    const rect = stage.container().getBoundingClientRect();
    return { x: (clientX - rect.left - stagePos.x) / scale, y: (clientY - rect.top - stagePos.y) / scale };
  }

  // Treffer-Lookup entlang der Eltern-Kette: Knoten oder Verbinder unter dem Zeiger (oder null).
  function targetAt(target: Konva.Node | null): { type: 'node' | 'connector'; id: string } | null {
    const stage = stageRef.current;
    let n: Konva.Node | null = target;
    while (n && n !== stage) {
      const id = n.id();
      if (id) {
        if (nodeMap.has(id)) return { type: 'node', id };
        if (connectorIds.has(id)) return { type: 'connector', id };
      }
      n = n.getParent();
    }
    return null;
  }

  /**
   * Zentrale Maus-Gestensteuerung (ersetzt die fruehere Auswahl/Hand-Werkzeugumschaltung):
   * - Linke Taste: kurz = auswaehlen/verbinden, ziehen/lange halten = Ansicht verschieben.
   *   Ein ausgewaehltes Objekt wird durch Ziehen verschoben (Konva-Drag der Group), solange
   *   nicht die Leertaste gedrueckt ist oder lange gehalten wurde (PanReady).
   * - Rechte Taste: kurz = Kontextmenue, ziehen/lange halten = Markierungsrahmen.
   * - Mittlere Taste: immer Navigieren (startMiddlePan).
   * Objekt-Move (Konva) und Navigieren (hier) schliessen sich ueber objectDragRef gegenseitig aus.
   */
  function beginPointerGesture(e: Konva.KonvaEventObject<MouseEvent>) {
    // Eine evtl. noch laufende Geste (z. B. zweite Taste) zuerst sauber beenden.
    gestureCleanupRef.current?.();
    const button = e.evt.button;
    if (button === 1) {
      startMiddlePan(e);
      return;
    }
    if (button !== 0 && button !== 2) return;
    // Resize-Anker des Transformers: Konva uebernimmt die Groessenaenderung, keine Geste starten.
    if (isTransformerTarget(e.target)) return;

    objectDragRef.current = false;
    const tgt = targetAt(e.target);
    const targetNodeId = tgt?.type === 'node' ? tgt.id : null;
    const targetConnectorId = tgt?.type === 'connector' ? tgt.id : null;
    const targetSelected = targetNodeId ? selectedIds.includes(targetNodeId) : false;
    const startX = e.evt.clientX;
    const startY = e.evt.clientY;
    const panOrig = { x: stagePos.x, y: stagePos.y };
    const shiftKey = e.evt.shiftKey;

    let moved = false;
    let longPressed = false;
    let panning = false;
    let marqueeActive = false;
    // Markierungs-Rechteck lokal in der Geste fuehren (nicht aus dem State lesen → kein Stale-Closure).
    let marqueeRect: MarqueeRect | null = null;
    let timer: number | null = null;

    const beginMarquee = () => {
      marqueeActive = true;
      const p = clientToWorld(startX, startY);
      if (p) {
        marqueeRect = { x1: p.x, y1: p.y, x2: p.x, y2: p.y };
        setMarquee(marqueeRect);
      }
    };

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) moved = true;

      if (button === 2) {
        if (moved && !marqueeActive) beginMarquee();
        if (marqueeActive && marqueeRect) {
          const p = clientToWorld(ev.clientX, ev.clientY);
          if (p) {
            marqueeRect = { ...marqueeRect, x2: p.x, y2: p.y };
            setMarquee(marqueeRect);
          }
        }
        return;
      }

      // Linke Taste: laeuft bereits ein Konva-Objekt-Move, hier nichts tun.
      if (objectDragRef.current) return;
      const shouldPan = panViaSpace || longPressed || !targetSelected || !draggable;
      if (moved && shouldPan) {
        if (!panning) {
          panning = true;
          setPanningActive(true);
        }
        props.onViewportChange(scale, { x: panOrig.x + dx, y: panOrig.y + dy });
      }
    };

    const onUp = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      gestureCleanupRef.current = null;

      const wasObjectDrag = objectDragRef.current;
      objectDragRef.current = false;
      if (panning) setPanningActive(false);
      setPanReady(false);

      if (button === 2) {
        // Markierungs-Box immer entfernen; ggf. auswerten. Das Kontextmenue oeffnet das
        // folgende contextmenu-Event (handleContextMenu) — bei Marquee per Ref unterdrueckt.
        rightMarqueeRef.current = marqueeActive;
        if (marqueeActive) {
          setMarquee(null);
          if (marqueeRect) finishMarquee(normalizeRect(marqueeRect));
        }
        return;
      }

      // Linke Taste: Objekt-Move/Navigieren/Long-Press → kein Tippen.
      if (wasObjectDrag || panning || moved || longPressed) return;
      if (props.commentMode) {
        // Kommentar-Modus: Tap setzt einen Pin (am Objekt oder freien Punkt).
        const p = clientToWorld(startX, startY);
        if (p) props.onCommentAt?.({ world: p, nodeId: targetNodeId });
        return;
      }
      if (targetNodeId) props.onNodeMouseDown(targetNodeId, shiftKey);
      else if (targetConnectorId) props.onConnectorMouseDown(targetConnectorId);
      else props.onEmptyMouseDown();
    };

    timer = window.setTimeout(() => {
      timer = null;
      if (objectDragRef.current) return; // Konva verschiebt bereits ein Objekt
      longPressed = true;
      if (button === 0) setPanReady(true); // Cursor 'grab'; folgendes Ziehen = Navigieren
      else beginMarquee();
    }, LONG_PRESS_MS);

    gestureCleanupRef.current = () => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  // Laufende Maus-Geste beim Unmount sauber beenden (Listener + Timer).
  useEffect(() => () => gestureCleanupRef.current?.(), []);

  function renderShape(node: SceneNode, highlight: boolean) {
    const env = ENV_COLORS[node.environmentType];
    const fill = node.fill ?? env.fill;
    const stroke = highlight ? '#0f172a' : (node.stroke ?? env.stroke);
    const labelPos = labelAlignment(node);
    const centeredLabel =
      node.label.length > 0 ? (
        <Text
          text={node.label}
          width={node.width}
          height={node.height}
          align={labelPos.align}
          verticalAlign={labelPos.verticalAlign}
          wrap="word"
          fontSize={node.fontSize ?? 14}
          fill={node.textColor ?? '#0f172a'}
          listening={false}
          padding={4}
        />
      ) : null;
    const envDot =
      node.environmentType !== 'neutral' ? (
        <Ellipse x={node.width - 9} y={9} radiusX={4} radiusY={4} fill={env.stroke} listening={false} />
      ) : null;

    switch (node.type) {
      case 'zone':
        return (
          <>
            <Rect
              width={node.width}
              height={node.height}
              fill={fill}
              opacity={0.4}
              stroke={stroke}
              strokeWidth={2}
              dash={node.environmentType === 'cloud' ? [10, 6] : undefined}
              cornerRadius={14}
            />
            <Text
              text={node.label || (node.environmentType === 'cloud' ? 'Cloud' : 'Vor Ort')}
              x={12}
              y={8}
              fontStyle="bold"
              fontSize={14}
              fill={env.stroke}
              listening={false}
            />
          </>
        );
      case 'circle': {
        const ir = inscribedRect(node.width, node.height);
        const circleLabel =
          node.label.length > 0 ? (
            <Text
              text={node.label}
              x={ir.x}
              y={ir.y}
              width={ir.width}
              height={ir.height}
              align={labelPos.align}
              verticalAlign={labelPos.verticalAlign}
              wrap="word"
              fontSize={node.fontSize ?? 14}
              fill={node.textColor ?? '#0f172a'}
              listening={false}
              padding={4}
            />
          ) : null;
        return (
          <>
            <Ellipse
              x={node.width / 2}
              y={node.height / 2}
              radiusX={node.width / 2}
              radiusY={node.height / 2}
              fill={fill}
              stroke={stroke}
              strokeWidth={2}
            />
            {circleLabel}
            {envDot}
          </>
        );
      }
      case 'cloud':
        return (
          <>
            <Path
              data={cloudPath(node.width, node.height)}
              fill={fill}
              stroke={stroke}
              strokeWidth={1}
            />
            {centeredLabel}
          </>
        );
      case 'image':
        return <EditorImageShape node={node} stroke={stroke} />;
      case 'text':
        return (
          <>
            <Rect
              width={node.width}
              height={node.height}
              fill={fill}
              stroke={stroke}
              strokeWidth={1}
              cornerRadius={6}
            />
            <Text
              text={node.label || ' '}
              width={node.width}
              height={node.height}
              align={labelPos.align}
              verticalAlign={labelPos.verticalAlign}
              wrap="word"
              fontSize={node.fontSize ?? 16}
              fill={node.textColor ?? '#0f172a'}
              listening={false}
              padding={6}
            />
            {envDot}
          </>
        );
      case 'vlan-legend': {
        const vlans = scene.vlans ?? [];
        const rowH = 22;
        const rowsTop = 50;
        // Ausgerichtete Spalten (lokal im Zeilen-Group bei x=10): Farbpunkt, ID, Name, Subnetz.
        const colIdX = 18;
        const colIdW = 42;
        const colNameX = 64;
        const colSubnetW = 110;
        const colSubnetX = node.width - 20 - colSubnetW;
        const colNameW = Math.max(16, colSubnetX - 6 - colNameX);
        const labelColor = '#64748b';
        return (
          <>
            <Rect
              width={node.width}
              height={node.height}
              fill={fill}
              stroke={stroke}
              strokeWidth={1}
              cornerRadius={8}
            />
            <Text
              text={node.label || t('editor.vlanLegendTitle')}
              x={10}
              y={8}
              fontStyle="bold"
              fontSize={13}
              fill="#0f172a"
              listening={false}
            />
            {vlans.length === 0 ? (
              <Text text="—" x={10} y={34} fontSize={12} fill="#94a3b8" listening={false} />
            ) : (
              <>
                <Group x={10} y={30} listening={false}>
                  <Text
                    text={t('editor.vlanManager.vlanId')}
                    x={colIdX}
                    width={colIdW}
                    fontSize={10}
                    fontStyle="bold"
                    fill={labelColor}
                    ellipsis
                    wrap="none"
                  />
                  <Text
                    text={t('editor.vlanManager.name')}
                    x={colNameX}
                    width={colNameW}
                    fontSize={10}
                    fontStyle="bold"
                    fill={labelColor}
                    ellipsis
                    wrap="none"
                  />
                  <Text
                    text={t('editor.vlanManager.subnet')}
                    x={colSubnetX}
                    width={colSubnetW}
                    fontSize={10}
                    fontStyle="bold"
                    fill={labelColor}
                    ellipsis
                    wrap="none"
                  />
                </Group>
                <Line
                  points={[10, 44, node.width - 10, 44]}
                  stroke="#e2e8f0"
                  strokeWidth={1}
                  listening={false}
                />
                {vlans.map((v, i) => (
                  <Group key={v.id} x={10} y={rowsTop + i * rowH} listening={false}>
                    <Circle x={6} y={6} radius={6} fill={v.color} />
                    <Text
                      text={String(v.vlanId)}
                      x={colIdX}
                      width={colIdW}
                      fontSize={12}
                      fill="#0f172a"
                      ellipsis
                      wrap="none"
                    />
                    <Text
                      text={v.name}
                      x={colNameX}
                      width={colNameW}
                      fontSize={12}
                      fill="#0f172a"
                      ellipsis
                      wrap="none"
                    />
                    {v.subnet ? (
                      <Text
                        text={v.subnet}
                        x={colSubnetX}
                        width={colSubnetW}
                        fontSize={12}
                        fill="#0f172a"
                        ellipsis
                        wrap="none"
                      />
                    ) : null}
                  </Group>
                ))}
              </>
            )}
          </>
        );
      }
      case 'switch-legend': {
        const source = node.switchLegend?.sourceNodeId
          ? scene.nodes.find((n) => n.id === node.switchLegend!.sourceNodeId && n.switch)
          : undefined;
        return <SwitchLegendShape node={node} sourceNode={source} vlans={scene.vlans} />;
      }
      case 'device': {
        const vlan = findVlan(scene.vlans, node.device?.vlanRef);
        const iconColor = vlan?.color ?? '#475569';
        const lines = deviceInfoLines(node, scene.vlans, scene.users);
        const lineH = 13;
        const iconSize = Math.max(16, Math.min(node.width, node.height * 0.45));
        const iconX = (node.width - iconSize) / 2;
        const textTop = iconSize + 4;
        return (
          <>
            {/* Hoerende Trefferflaeche mit dauerhaft dezentem Rahmen: macht die ganze Box
                anklick-/ziehbar (Icon/Text hoeren nicht) und zeigt, wo Verbinder andocken.
                Bei Auswahl wird der Rahmen kraeftig. */}
            <Rect
              width={node.width}
              height={node.height}
              fill={highlight ? 'transparent' : 'rgba(148,163,184,0.06)'}
              stroke={highlight ? '#0f172a' : '#cbd5e1'}
              strokeWidth={highlight ? 1.5 : 1}
              cornerRadius={8}
            />
            <DeviceShape node={node} size={iconSize} color={iconColor} x={iconX} y={2} />
            {lines.map((ln, i) => {
              let text: string;
              let color: string;
              let bold = false;
              if (ln.type === 'name') {
                text = ln.text;
                color = '#0f172a';
                bold = true;
              } else if (ln.type === 'vlan') {
                text = `VLAN: ${ln.vlanId}`;
                color = ln.color;
              } else if (ln.type === 'ip') {
                const modeWord = t(`editor.device.ipModeOption.${ln.mode}`);
                text = ln.mode === 'dhcp' || !ln.value ? `IP: ${modeWord}` : `IP: ${ln.value} (${modeWord})`;
                color = '#475569';
              } else if (ln.type === 'dns') {
                text = `${t('editor.device.dns')}: ${ln.value}`;
                color = '#475569';
              } else if (ln.type === 'user') {
                text = `${t('editor.device.user')}: ${ln.value}`;
                color = '#475569';
              } else {
                // ln.type === 'ports' — kompakte Liste ohne Praefix (Nutzerwunsch)
                text = ln.text;
                color = '#475569';
              }
              return (
                <Text
                  key={ln.type}
                  text={text}
                  x={2}
                  y={textTop + i * lineH}
                  width={node.width - 4}
                  align="center"
                  fontSize={10}
                  fontStyle={bold ? 'bold' : 'normal'}
                  fill={color}
                  listening={false}
                  ellipsis={ln.type !== 'ports'}
                  wrap={ln.type === 'ports' ? 'word' : 'none'}
                />
              );
            })}
          </>
        );
      }
      case 'switch':
        return (
          <SwitchShape
            node={node}
            vlans={scene.vlans}
            highlight={highlight}
            selectedPortIndex={props.selectedPort?.nodeId === node.id ? props.selectedPort.portIndex : null}
            onPortMouseDown={(portIndex) => props.onPortMouseDown(node.id, portIndex)}
          />
        );
      case 'host':
        return (
          <HostShape
            node={node}
            vlans={scene.vlans}
            allNodes={scene.nodes}
            highlight={highlight}
            activeLabel={t('editor.host.active')}
            inactiveLabel={t('editor.host.inactive')}
            cpuOverLabel={t('editor.host.cpuOver')}
            ramOverLabel={t('editor.host.ramOver')}
            vlanLabel="VLAN"
            storageLabel="Clusterstorage"
          />
        );
      case 'clusterstorage':
        return (
          <ClusterStorageShape
            node={node}
            allNodes={scene.nodes}
            highlight={highlight}
            kindLabels={t('editor.clusterStorage.kindOption', { returnObjects: true }) as Record<string, string>}
            standLabel={t('editor.clusterStorage.importedAt')}
            fullLabel={t('editor.clusterStorage.full')}
            vmCountLabel={(n) => t('editor.clusterStorage.vmCount', { count: n })}
            redundancyLabels={t('editor.clusterStorage.volRedundancyOption', { returnObjects: true }) as Record<string, string>}
          />
        );
      case 'firewall':
        return (
          <FirewallShape
            node={node}
            highlight={highlight}
            selectedPortIndex={props.selectedPort?.nodeId === node.id ? props.selectedPort.portIndex : null}
            onPortMouseDown={(portIndex) => props.onPortMouseDown(node.id, portIndex)}
          />
        );
      case 'firewall-legend': {
        const source = node.firewallLegend?.sourceNodeId
          ? scene.nodes.find((n) => n.id === node.firewallLegend!.sourceNodeId && n.firewall)
          : undefined;
        return <FirewallLegendShape node={node} sourceNode={source} />;
      }
      default:
        return (
          <>
            <Rect
              width={node.width}
              height={node.height}
              fill={fill}
              stroke={stroke}
              strokeWidth={2}
              cornerRadius={6}
            />
            {centeredLabel}
            {envDot}
          </>
        );
    }
  }

  /** Snap-Anker: alle Knoten außer der gerade bewegten Menge. */
  function otherBoxes(excluded: ReadonlySet<string>): Box[] {
    return scene.nodes.filter((n) => !excluded.has(n.id)).map(toBox);
  }

  function movingSetFor(grabbedId: string): string[] {
    const nodeIds = selectedIds.filter((id) => nodeMap.has(id));
    if (nodeIds.includes(grabbedId)) return nodeIds;
    // Auswahl-State kann beim Drag-Start noch nicht aktualisiert sein →
    // mindestens die eigene Gruppe mitnehmen.
    return expandSelection(scene.nodes, [grabbedId]).filter((id) => nodeMap.has(id));
  }

  /** Gestrichelte, zoom-invariante Outline in der Farbe des fremden Teilnehmers (Phase 3). */
  function renderRemoteOutline(node: SceneNode, color: string) {
    const pad = 3 / scale;
    const common = {
      stroke: color,
      strokeWidth: 2 / scale,
      dash: [6 / scale, 4 / scale],
      listening: false,
    };
    return node.type === 'circle' ? (
      <Ellipse
        x={node.width / 2}
        y={node.height / 2}
        radiusX={node.width / 2 + pad}
        radiusY={node.height / 2 + pad}
        {...common}
      />
    ) : (
      <Rect x={-pad} y={-pad} width={node.width + pad * 2} height={node.height + pad * 2} cornerRadius={8} {...common} />
    );
  }

  function renderNode(node: SceneNode) {
    const highlight = !exporting && (selectedIds.includes(node.id) || pendingFromId === node.id);
    const remoteColor = exporting ? undefined : props.remoteSelections?.get(node.id);
    return (
      <Group
        key={node.id}
        id={node.id}
        x={node.x}
        y={node.y}
        // Verschiebbar nur am ausgewaehlten Objekt; Leertaste/PanReady erzwingen Navigieren statt Move.
        draggable={draggable && selectedIds.includes(node.id) && !panViaSpace && !panReady}
        onDragStart={() => {
          objectDragRef.current = true; // unterdrueckt das parallele Navigieren der Geste
          const movingIds = movingSetFor(node.id);
          const orig = new Map<string, { x: number; y: number }>();
          for (const id of movingIds) {
            const box = nodeMap.get(id);
            if (box) orig.set(id, { x: box.x, y: box.y });
          }
          if (!orig.has(node.id)) orig.set(node.id, { x: node.x, y: node.y });
          dragRef.current = { grabbedId: node.id, orig };
        }}
        onDragMove={(e) => {
          const g = e.target;
          const st = dragRef.current;
          const excluded = new Set(st ? st.orig.keys() : [node.id]);
          // Snapping nur am gegriffenen Knoten.
          const snap = computeSnap(
            { x: g.x(), y: g.y(), width: node.width, height: node.height },
            otherBoxes(excluded),
            SNAP_THRESHOLD,
          );
          g.position({ x: snap.x, y: snap.y });
          setGuides(snap.guides);
          // Übrige Auswahl imperativ mitbewegen (Delta des gegriffenen Knotens).
          if (st && st.orig.size > 1) {
            const origGrabbed = st.orig.get(node.id)!;
            const dx = snap.x - origGrabbed.x;
            const dy = snap.y - origGrabbed.y;
            for (const [id, pos] of st.orig) {
              if (id === node.id) continue;
              stageRef.current?.findOne(`#${id}`)?.position({ x: pos.x + dx, y: pos.y + dy });
            }
          }
        }}
        onDragEnd={(e) => {
          setGuides([]);
          const g = e.target;
          const st = dragRef.current;
          dragRef.current = null;
          const excluded = new Set(st ? st.orig.keys() : [node.id]);
          const snap = computeSnap(
            { x: g.x(), y: g.y(), width: node.width, height: node.height },
            otherBoxes(excluded),
            SNAP_THRESHOLD,
          );
          const finalX = snap.guides.length > 0 ? Math.round(snap.x) : snapToGrid(g.x());
          const finalY = snap.guides.length > 0 ? Math.round(snap.y) : snapToGrid(g.y());
          // Gesammelter Commit: gegriffener Knoten gesnappt, Rest mit gleichem Delta.
          const changes: { id: string; patch: Partial<SceneNode> }[] = [{ id: node.id, patch: { x: finalX, y: finalY } }];
          if (st && st.orig.size > 1) {
            const origGrabbed = st.orig.get(node.id)!;
            const dx = finalX - origGrabbed.x;
            const dy = finalY - origGrabbed.y;
            for (const [id, pos] of st.orig) {
              if (id === node.id) continue;
              changes.push({ id, patch: { x: Math.round(pos.x + dx), y: Math.round(pos.y + dy) } });
            }
          }
          props.onNodesChange(changes);
        }}
      >
        {renderShape(node, highlight)}
        {remoteColor ? renderRemoteOutline(node, remoteColor) : null}
      </Group>
    );
  }

  function connectorPoints(connector: SceneDoc['connectors'][number]) {
    const fromCenter = resolveEndpoint(connector.from, nodeMap);
    const toCenter = resolveEndpoint(connector.to, nodeMap);
    if (!fromCenter || !toCenter) return null;
    let from = fromCenter;
    let to = toCenter;
    if ('nodeId' in connector.from) {
      const box = nodeMap.get(connector.from.nodeId);
      const sw = switchMap.get(connector.from.nodeId);
      const fromFw = firewallMap.get(connector.from.nodeId);
      if (box && connector.from.portIndex !== undefined && sw) {
        from = portAnchorPoint(box, sw, connector.from.portIndex);
      } else if (box && connector.from.portIndex !== undefined && fromFw) {
        from = firewallAnchorPoint(box, fromFw.interfaces, connector.from.portIndex);
      } else if (box) {
        from = edgeAnchor(box, toCenter, { port: connector.from.port, shape: shapeMap.get(connector.from.nodeId) });
      }
    }
    if ('nodeId' in connector.to) {
      const box = nodeMap.get(connector.to.nodeId);
      const sw = switchMap.get(connector.to.nodeId);
      const toFw = firewallMap.get(connector.to.nodeId);
      if (box && connector.to.portIndex !== undefined && sw) {
        to = portAnchorPoint(box, sw, connector.to.portIndex);
      } else if (box && connector.to.portIndex !== undefined && toFw) {
        to = firewallAnchorPoint(box, toFw.interfaces, connector.to.portIndex);
      } else if (box) {
        to = edgeAnchor(box, fromCenter, { port: connector.to.port, shape: shapeMap.get(connector.to.nodeId) });
      }
    }
    return { from, to };
  }

  function renderConnector(connector: SceneDoc['connectors'][number]) {
    const pts = connectorPoints(connector);
    if (!pts) return null;
    const { from, to } = pts;
    const selected = selectedIds.includes(connector.id);
    const remoteColor = exporting ? undefined : props.remoteSelections?.get(connector.id);
    const isOrthogonal = (connector.routing ?? 'straight') === 'orthogonal';
    const route = isOrthogonal ? orthogonalRoute(from, to, connector.bendRatio ?? 0.5) : [from, to];
    // Brücken-Bögen an Kreuzungen einsetzen (Basis-route bleibt für das Bend-Handle).
    // Enden mit Pfeilspitze etwas zurueckziehen, damit die Spitze nicht ins Objekt ragt.
    const display = retractArrowEnds(applyBridges(route, bridgeMarkers.get(connector.id) ?? []), {
      start: connector.arrowStart ?? false,
      end: connector.arrowEnd ?? true,
      gap: ARROW_GAP,
    });
    const points = display.flatMap((p) => [p.x, p.y]);
    const baseColor = connector.cableType
      ? cableColor(connector.cableType, connector.fiberMode)
      : (connector.stroke ?? '#475569');
    const stroke = selected ? '#0f172a' : baseColor;
    const strokeWidth = (connector.strokeWidth ?? 2) + (selected ? 1 : 0);
    const style = connectorLineStyle(connector);
    const dash = style === 'dashed' ? [8, 6] : style === 'dotted' ? [2, 6] : undefined;
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;
    return (
      <Group key={connector.id} id={connector.id}>
        {remoteColor ? (
          <Line
            points={points}
            stroke={remoteColor}
            strokeWidth={(connector.strokeWidth ?? 2) + 5 / scale}
            opacity={0.45}
            lineCap="round"
            lineJoin="round"
            listening={false}
          />
        ) : null}
        <Arrow
          points={points}
          stroke={stroke}
          fill={stroke}
          strokeWidth={strokeWidth}
          dash={dash}
          pointerAtBeginning={connector.arrowStart ?? false}
          pointerAtEnding={connector.arrowEnd ?? true}
          pointerLength={10}
          pointerWidth={10}
          hitStrokeWidth={14}
          lineJoin="round"
        />
        {connector.label ? (
          <Text
            text={connector.label}
            x={midX - 60}
            y={midY - 18}
            width={120}
            align="center"
            fontSize={12}
            fill="#0f172a"
            listening={false}
          />
        ) : null}
        {connector.labelBelow ? (
          <Text
            text={connector.labelBelow}
            x={midX - 60}
            y={midY + 6}
            width={120}
            align="center"
            fontSize={12}
            fill="#0f172a"
            listening={false}
          />
        ) : null}
        {selected && isOrthogonal && draggable ? renderBendHandle(connector, from, to, route) : null}
      </Group>
    );
  }

  /** Drag-Handle auf dem Mittelsegment eines orthogonalen Verbinders (achsgebunden). */
  function renderBendHandle(
    connector: SceneDoc['connectors'][number],
    from: { x: number; y: number },
    to: { x: number; y: number },
    route: { x: number; y: number }[],
  ) {
    const horizontal = Math.abs(to.x - from.x) >= Math.abs(to.y - from.y);
    const handleX = horizontal ? route[1]!.x : (from.x + to.x) / 2;
    const handleY = horizontal ? (from.y + to.y) / 2 : route[1]!.y;
    // dragBoundFunc arbeitet in absoluten (Stage-)Koordinaten.
    const fixedAbsX = stagePos.x + handleX * scale;
    const fixedAbsY = stagePos.y + handleY * scale;
    return (
      <Circle
        x={handleX}
        y={handleY}
        radius={6}
        fill="#ffffff"
        stroke="#0f172a"
        strokeWidth={1.5}
        draggable
        // Eigenes Drag-Handle: die zentrale Navigations-Geste hier nicht ausloesen.
        onMouseDown={(e) => {
          e.cancelBubble = true;
        }}
        dragBoundFunc={(pos) => (horizontal ? { x: pos.x, y: fixedAbsY } : { x: fixedAbsX, y: pos.y })}
        onDragEnd={(e) => {
          const node = e.target;
          const raw = horizontal
            ? (node.x() - from.x) / (to.x - from.x || 1)
            : (node.y() - from.y) / (to.y - from.y || 1);
          const ratio = Math.min(1, Math.max(0, Math.round(raw * 1000) / 1000));
          props.onConnectorChange(connector.id, { bendRatio: ratio });
        }}
      />
    );
  }

  const zones = scene.nodes.filter((n) => n.type === 'zone');
  const others = scene.nodes.filter((n) => n.type !== 'zone');
  // Port-Kabel ueber den Switchen zeichnen, damit sie sichtbar direkt am Port ansetzen.
  // Uebrige Verbindungen bleiben unter den Knoten (Knoten bleiben gut anklickbar).
  const isPortConnector = (c: SceneDoc['connectors'][number]) =>
    ('nodeId' in c.from && c.from.portIndex !== undefined) || ('nodeId' in c.to && c.to.portIndex !== undefined);
  const underConnectors = scene.connectors.filter((c) => !isPortConnector(c));
  const overConnectors = scene.connectors.filter(isPortConnector);

  // Linien-Bruecken (Baustein D): Basis-Routen aufloesen, Kreuzungen berechnen, je Connector
  // gruppieren. Wird zur Anzeigezeit bestimmt (nicht persistiert); renderConnector liest daraus.
  const resolvedConnectors: ResolvedConnector[] = [];
  for (const c of scene.connectors) {
    const pts = connectorPoints(c);
    if (!pts) continue;
    const route =
      (c.routing ?? 'straight') === 'orthogonal'
        ? orthogonalRoute(pts.from, pts.to, c.bendRatio ?? 0.5)
        : [pts.from, pts.to];
    resolvedConnectors.push({ id: c.id, points: route, bridge: c.bridge });
  }
  const bridgeMarkers = new Map<string, BridgeMarker[]>();
  for (const m of computeLineBridges(resolvedConnectors, { enabled: scene.lineBridges !== false })) {
    const arr = bridgeMarkers.get(m.connectorId);
    if (arr) arr.push(m);
    else bridgeMarkers.set(m.connectorId, [m]);
  }

  const marqueeBox = marquee ? normalizeRect(marquee) : null;

  return (
    <Stage
      ref={stageRef}
      width={viewportWidth}
      height={viewportHeight}
      scaleX={scale}
      scaleY={scale}
      x={stagePos.x}
      y={stagePos.y}
      onWheel={handleWheel}
      // Browser-Kontextmenue unterdruecken; App-Menue an der Zeigerposition (ausser nach Markierung).
      onContextMenu={handleContextMenu}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={beginPointerGesture}
      onMouseMove={() => {
        const p = worldPointer();
        props.onPointerWorldMove?.(p);
        // Nur die Connect-Vorschaulinie folgt dem Zeiger; Navigieren/Markieren laeuft ueber die Geste.
        if (!pendingFrom) return;
        if (p) setConnectPointer(p);
      }}
      onMouseLeave={() => props.onPointerWorldMove?.(null)}
      style={{
        background: '#e2e8f0',
        cursor: panningActive
          ? 'grabbing'
          : panViaSpace || panReady
            ? 'grab'
            : connectMode || props.commentMode
              ? 'crosshair'
              : 'default',
      }}
    >
      {/* Seiten-Blatt mit Schatten */}
      <Layer listening={false}>
        <Rect
          x={canvas.x}
          y={canvas.y}
          width={canvas.width}
          height={canvas.height}
          fill="#ffffff"
          stroke="#cbd5e1"
          strokeWidth={1}
          shadowColor="#0f172a"
          shadowBlur={12}
          shadowOpacity={0.15}
          shadowOffsetY={4}
        />
        {gridLines.map((pts, i) => (
          <Line key={i} points={pts} stroke="#eef2f7" strokeWidth={1} />
        ))}
      </Layer>
      <Layer>
        {zones.map(renderNode)}
        {underConnectors.map(renderConnector)}
        {others.map(renderNode)}
        {overConnectors.map(renderConnector)}
        <Transformer
          ref={trRef}
          rotateEnabled={false}
          flipEnabled={false}
          ignoreStroke
          boundBoxFunc={(oldBox, newBox) => (newBox.width < 20 || newBox.height < 20 ? oldBox : newBox)}
          onTransformEnd={() => {
            const tr = trRef.current;
            if (!tr) return;
            // Gesammelter Commit für alle transformierten Knoten (1 setScene).
            const changes = tr.nodes().map((g) => {
              const box = nodeMap.get(g.id());
              const scaleX = g.scaleX();
              const scaleY = g.scaleY();
              g.scaleX(1);
              g.scaleY(1);
              return {
                id: g.id(),
                patch: {
                  x: snapToGrid(g.x()),
                  y: snapToGrid(g.y()),
                  width: Math.max(20, Math.round((box?.width ?? 20) * scaleX)),
                  height: Math.max(20, Math.round((box?.height ?? 20) * scaleY)),
                },
              };
            });
            props.onNodesChange(changes);
          }}
        />
      </Layer>
      {/* Ausrichtungs-Hilfslinien + Connect-Vorschau + Auswahl-Rechteck (beim Export ausblenden) */}
      {!exporting && (
      <Layer listening={false}>
        {guides.map((g, i) =>
          g.axis === 'x' ? (
            <Line
              key={`gx${i}`}
              points={[g.position, canvas.y, g.position, canvas.y + canvas.height]}
              stroke="#ec4899"
              strokeWidth={2 / scale}
              dash={[6 / scale, 4 / scale]}
            />
          ) : (
            <Line
              key={`gy${i}`}
              points={[canvas.x, g.position, canvas.x + canvas.width, g.position]}
              stroke="#ec4899"
              strokeWidth={2 / scale}
              dash={[6 / scale, 4 / scale]}
            />
          ),
        )}
        {(() => {
          if (!pendingFrom || !connectPointer) return null;
          const src = connectSourceAnchor();
          if (!src) return null;
          return (
            <>
              <Line
                points={[src.x, src.y, connectPointer.x, connectPointer.y]}
                stroke="#2563eb"
                strokeWidth={2 / scale}
                dash={[8 / scale, 6 / scale]}
              />
              <Circle x={src.x} y={src.y} radius={5 / scale} fill="#2563eb" />
            </>
          );
        })()}
        {marqueeBox && (
          <Rect
            x={marqueeBox.x}
            y={marqueeBox.y}
            width={marqueeBox.width}
            height={marqueeBox.height}
            stroke="#0f172a"
            strokeWidth={1 / scale}
            dash={[6 / scale, 4 / scale]}
            fill="rgba(15, 23, 42, 0.06)"
          />
        )}
      </Layer>
      )}
      {/* Fremde Live-Cursor (Kollaboration); beim Export ausgeblendet. */}
      {!exporting && (props.remoteCursors?.length ?? 0) > 0 && (
        <RemoteCursorsLayer cursors={props.remoteCursors!} scale={scale} />
      )}
    </Stage>
  );
}
